import { Box, Skeleton, Typography } from "@mui/material";
import { ArrowUpRight } from "lucide-react";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

/**
 * How much of each thing there is — and the way into it.
 *
 * These are counts AND navigation, which is why there is no separate row of
 * "quick action" buttons duplicating the rail: a tile that says "Customers
 * 42" and opens the Customers page is one control doing both jobs. The rail
 * badges the same numbers, on purpose; the command center badges its own
 * rows the same way, and a number you can act on is worth showing twice.
 *
 * Quotes get their own tile because they are their own section here — and
 * since spec 2026-09-09 §3.2 they are a different KIND of document from the
 * invoices beside them: XOP Networks' quotes TO this reseller, counted open
 * only, rather than the reseller's own quotes to its customers.
 */

export interface CountTile {
  label: string;
  /** Already formatted — an unavailable count is an em dash, not a zero. */
  value: string;
  /** The line under the number: what the count is made of, when the CRM
   * gave us enough to say. */
  sub: string | null;
  to: string;
  testId: string;
}

export function CountTiles({
  tiles,
  pending = false,
}: {
  tiles: readonly CountTile[];
  /** See `Ledger` — a skeleton while loading, an em dash only when the
   * figure genuinely is not available. */
  pending?: boolean;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", sm: `repeat(${tiles.length}, 1fr)` },
      }}
    >
      {tiles.map((tile) => (
        <Box
          key={tile.label}
          component={RouterLink}
          to={tile.to}
          data-testid={tile.testId}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            px: 2.5,
            py: 2,
            textDecoration: "none",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            transition: (t) =>
              t.transitions.create(["border-color", "transform", "box-shadow"], {
                duration: 160,
                // Exponential-ish ease-out: quick to leave, soft to arrive.
                easing: "cubic-bezier(0.22, 1, 0.36, 1)",
              }),
            "&:hover": {
              borderColor: (t) => t.palette.accent[300],
              transform: "translateY(-2px)",
              boxShadow: 1,
            },
            "&:hover .tile-arrow": { transform: "translate(2px, -2px)", opacity: 1 },
            "&:focus-visible": {
              outline: (t) => `2px solid ${t.palette.accent[500]}`,
              outlineOffset: 2,
            },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
              "&:hover": { transform: "none" },
              "&:hover .tile-arrow": { transform: "none" },
            },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="p"
              sx={{ fontSize: 13, fontWeight: 500, color: "text.secondary" }}
            >
              {tile.label}
            </Typography>
            {pending ? (
              <Skeleton variant="text" width={64} sx={{ mt: 0.25, fontSize: "1.5rem" }} />
            ) : (
            <Typography
              component="p"
              sx={{
                mt: 0.25,
                fontFamily: (t) => t.typography.h1.fontFamily,
                fontSize: "1.5rem",
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                color: "text.primary",
              }}
            >
              {tile.value}
            </Typography>
            )}
            {!pending && tile.sub && (
              <Typography component="p" sx={{ mt: 0.25, fontSize: 12, color: "text.secondary" }}>
                {tile.sub}
              </Typography>
            )}
          </Box>

          <Box
            className="tile-arrow"
            aria-hidden
            sx={{
              display: "flex",
              color: "text.disabled",
              opacity: 0.65,
              transition: (t) =>
                t.transitions.create(["transform", "opacity"], {
                  duration: 160,
                  easing: "cubic-bezier(0.22, 1, 0.36, 1)",
                }),
            }}
          >
            <ArrowUpRight size={18} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
